import React from 'react';

import {
    InputRequiredLegend as DSInputRequiredLegend,
    InputRequiredLegendProps as DSInputRequiredLegendProps,
} from '@lumx/react';

import { GLOBAL, useTranslate } from '@lumapps/translations';

/** Input required legend props */
export interface InputRequiredLegendProps extends Omit<DSInputRequiredLegendProps, 'label'> {
    /** Legend text (defaults to the translated "Indicates a required field") */
    label?: DSInputRequiredLegendProps['label'];
}

/**
 * Legend explaining the required field marker (`*`) shown on the label of required inputs.
 *
 * Place it once at the top of a form that contains required fields, so that sighted users know what
 * the asterisk means (a11y, RGAA criterion 11.10). The required state itself is still conveyed to
 * assistive technologies by the `required` attribute of each input.
 *
 * If the required fields are displayed conditionally, only show the legend while at least one of
 * them is visible.
 *
 * LumApps wrapper around the DS InputRequiredLegend: the `label` defaults to the translated
 * "Indicates a required field" (`GLOBAL.INDICATES_REQUIRED_FIELD`), so forms share the same wording.
 *
 * @example
 * <form>
 *     <InputRequiredLegend />
 *     <TextField label="Name" isRequired />
 * </form>
 *
 * @family Inputs
 */
export const InputRequiredLegend = React.forwardRef<HTMLParagraphElement, InputRequiredLegendProps>((props, ref) => {
    const { translateKey } = useTranslate();

    const label = props.label ?? translateKey(GLOBAL.INDICATES_REQUIRED_FIELD);

    return <DSInputRequiredLegend ref={ref} {...props} label={label} />;
});
